"use client";

import { Globe2, Layers, Trash2 } from "@/components/icons";
import type { BuilderHighlight, ProductVariant, SessionBuilderCopy, SessionRow } from "./types";

export const BUILDER_HIGHLIGHTS: BuilderHighlight[] = [
  {
    icon: Layers,
    title: "Bulk creation",
    description: "Spin up a whole batch of sessions in a single action, with no one-by-one setup.",
  },
  {
    icon: Globe2,
    title: "Any mix of locations",
    description:
      "One session no longer means one location. A single batch can span different countries, locations, and access points.",
  },
  {
    icon: Trash2,
    title: "Instant cleanup",
    description: "Delete sessions one by one, or clear the whole batch in one click.",
  },
];

export const SESSION_BUILDER_COPY: Record<ProductVariant, SessionBuilderCopy> = {
  mobile: {
    eyebrow: "Session Builder",
    heading: "Build entire session fleets in one go",
    body: "Session Builder creates multiple sessions at once across different cities and access points worldwide, instead of configuring them one at a time. And when you're done, they're gone just as fast.",
    locations: [
      { id: "us-dal", label: "Dallas, TX", flag: "🇺🇸" },
      { id: "ca-tor", label: "Toronto, Canada", flag: "🇨🇦" },
      { id: "gb-lon", label: "London, UK", flag: "🇬🇧" },
      { id: "de-ber", label: "Berlin, Germany", flag: "🇩🇪" },
      { id: "us-mia", label: "Miami, FL", flag: "🇺🇸" },
      { id: "us-sea", label: "Seattle, WA", flag: "🇺🇸" },
      { id: "us-atl", label: "Atlanta, GA", flag: "🇺🇸" },
    ],
    accessPoints: ["dallas-4g-01", "toronto-5g-01", "london-4g-02", "atlanta-5g-02"],
  },
  residential: {
    eyebrow: "Session Builder",
    heading: "Multiple countries. One batch of sessions.",
    body: "Session Builder creates multiple sessions at once across different countries, locations, and access points, instead of configuring them one at a time. And when you're done, they're gone just as fast.",
    locations: [
      { id: "us", label: "United States", flag: "🇺🇸" },
      { id: "gb", label: "United Kingdom", flag: "🇬🇧" },
      { id: "de", label: "Germany", flag: "🇩🇪" },
      { id: "in", label: "India", flag: "🇮🇳" },
    ],
    accessPoints: ["us-pool-01", "eu-pool-02", "in-pool-03"],
  },
};

export const MAX_SESSIONS = 12;

export function buildSession(
  locationId: string,
  copy: SessionBuilderCopy,
  serial: number
): SessionRow {
  const location = copy.locations.find((l) => l.id === locationId) ?? copy.locations[0];
  return {
    id: `${location.id}-${serial}`,
    name: `${location.id}-${String(serial).padStart(3, "0")}`,
    locationLabel: location.label,
    flag: location.flag,
    accessPoint: copy.accessPoints[serial % copy.accessPoints.length],
  };
}

export function seedSessions(variant: ProductVariant): SessionRow[] {
  const copy = SESSION_BUILDER_COPY[variant];
  return [
    buildSession(copy.locations[0].id, copy, 101),
    buildSession(copy.locations[1].id, copy, 102),
    buildSession(copy.locations[2].id, copy, 103),
  ];
}
